import { createSelector } from '@ngrx/store';
import { docsFeature } from '../docs/docs.reducer';
import { docKey } from '../docs/docs.normalize';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import {
  ChatMessage,
  RepoOverviewState,
  ReposSettings,
  normalizeReposSettings,
  normalizeReposState,
} from './repos.models';

const { selectEntities, selectKeyIndex, selectStatusByKey, selectErrorByKey } = docsFeature;

function dataFor(
  entities: ReturnType<typeof selectEntities>,
  keyIndex: Record<string, string>,
  pid: string | null,
  key: string
): any {
  if (!pid) return null;
  const id = keyIndex[docKey(pid, key)];
  return id ? entities.entities[id]?.data ?? null : null;
}

export const selectReposSettings = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): ReposSettings => normalizeReposSettings(dataFor(e, k, pid, 'repos.settings'))
);

export const selectReposState = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): RepoOverviewState => normalizeReposState(dataFor(e, k, pid, 'repos.state'))
);

export const selectReposChatMessages = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): ChatMessage[] => {
    const d = dataFor(e, k, pid, 'repos.chat');
    return Array.isArray(d?.messages) ? (d.messages as ChatMessage[]) : [];
  }
);

export const selectReposProjectDefinition = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => dataFor(e, k, pid, 'define.definition')
);

export const selectHasGitHubInstallation = createSelector(selectReposSettings, (s) => {
  const id = s.github.installationId;
  return typeof id === 'number' && Number.isFinite(id) && id > 0;
});

export const selectReposLoading = createSelector(selectActiveProjectId, selectStatusByKey, (pid, st) =>
  pid ? st[docKey(pid, 'repos.settings')] === 'loading' : false
);

export const selectReposError = createSelector(selectActiveProjectId, selectErrorByKey, (pid, er) =>
  pid ? er[docKey(pid, 'repos.settings')] ?? null : null
);
